import { Alert, Button, Spin, Tabs } from 'antd'
import { DownloadOutlined, FileOutlined } from '@ant-design/icons'
import { memo, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import apiClient from '../../Utils/apiClient'
import './Drive.scss'

const EXT_MIME = {
  // documents
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  odt: 'application/vnd.oasis.opendocument.text',
  rtf: 'application/rtf',
  // sheets
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  // slides
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odp: 'application/vnd.oasis.opendocument.presentation',
  // images
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  avif: 'image/avif',
  apng: 'image/apng',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  // media
  mp4: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac',
  // text / web / code
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
  json: 'application/json',
  xml: 'application/xml',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  js: 'text/javascript',
  jsx: 'text/javascript',
  ts: 'text/typescript',
  tsx: 'text/typescript',
  py: 'text/x-python',
  java: 'text/x-java-source',
  c: 'text/x-c',
  cpp: 'text/x-c',
  h: 'text/x-c',
  cs: 'text/plain',
  go: 'text/plain',
  rs: 'text/plain',
  php: 'text/plain',
  rb: 'text/plain',
  sh: 'text/x-shellscript',
  yml: 'text/yaml',
  yaml: 'text/yaml',
  ini: 'text/plain',
  conf: 'text/plain',
  log: 'text/plain',
  sql: 'text/plain',
  // archives
  zip: 'application/zip',
  jar: 'application/java-archive'
}

const IMAGE_EXTS = new Set([
  'png',
  'jpg',
  'jpeg',
  'jfif',
  'gif',
  'webp',
  'bmp',
  'svg',
  'ico',
  'avif',
  'apng',
  'tif',
  'tiff',
  'heic',
  'heif'
])

const VIDEO_EXTS = new Set(['mp4', 'webm', 'ogv', 'mov', 'm4v'])
const AUDIO_EXTS = new Set(['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'])
const EXCEL_EXTS = new Set(['xls', 'xlsx', 'csv', 'tsv', 'ods'])
const WORD_EXTS = new Set(['doc', 'docx', 'odt', 'rtf'])
const PPT_EXTS = new Set(['ppt', 'pptx', 'odp'])
const TEXT_EXTS = new Set([
  'txt',
  'md',
  'markdown',
  'json',
  'xml',
  'html',
  'htm',
  'css',
  'js',
  'jsx',
  'ts',
  'tsx',
  'py',
  'java',
  'c',
  'cpp',
  'h',
  'cs',
  'go',
  'rs',
  'php',
  'rb',
  'sh',
  'yml',
  'yaml',
  'ini',
  'conf',
  'log',
  'sql',
  'csv',
  'tsv'
])
const ZIP_EXTS = new Set(['zip', 'jar'])

const getExt = (fileName = '') => {
  const parts = String(fileName).split('.')

  if (parts.length < 2) {
    return ''
  }

  return parts.pop().toLowerCase()
}

const guessFileType = (fileName, fileType) => {
  const fromExt = EXT_MIME[getExt(fileName)]

  if (fromExt) {
    return fromExt
  }

  if (fileType && fileType !== 'application/octet-stream') {
    return fileType
  }

  return 'application/octet-stream'
}

const sniffMimeFromBytes = (buffer) => {
  if (!buffer || buffer.byteLength < 4) {
    return null
  }

  const bytes = new Uint8Array(buffer)
  const asHex = [...bytes.slice(0, 12)].map((b) => b.toString(16).padStart(2, '0')).join('')

  if (asHex.startsWith('89504e47')) {
    return 'image/png'
  }

  if (asHex.startsWith('ffd8ff')) {
    return 'image/jpeg'
  }

  if (asHex.startsWith('47494638')) {
    return 'image/gif'
  }

  if (asHex.startsWith('52494646') && asHex.includes('57454250')) {
    return 'image/webp'
  }

  if (asHex.startsWith('424d')) {
    return 'image/bmp'
  }

  if (asHex.startsWith('25504446')) {
    return 'application/pdf'
  }

  if (asHex.startsWith('504b0304') || asHex.startsWith('504b0506') || asHex.startsWith('504b0708')) {
    return 'application/zip'
  }

  return null
}

const looksLikeText = (buffer) => {
  if (!buffer || !buffer.byteLength) {
    return false
  }

  const sample = new Uint8Array(buffer.slice(0, Math.min(buffer.byteLength, 2048)))
  let odd = 0

  for (let i = 0; i < sample.length; i += 1) {
    const code = sample[i]

    if (code === 0) {
      return false
    }

    if (code < 7 || (code > 14 && code < 32 && code !== 9 && code !== 10 && code !== 13)) {
      odd += 1
    }
  }

  return odd / sample.length < 0.1
}

const stripXml = (xml = '') =>
  xml
    .replace(/<a:t[^>]*>/g, '')
    .replace(/<\/a:t>/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim()

const formatBytes = (value) => {
  const size = Number(value) || 0

  if (size < 1024) {
    return `${size} B`
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const DownloadFallback = ({ blobUrl, fileName, fileType, size, reason }) => (
  <div style={{ padding: 32, textAlign: 'center' }}>
    <FileOutlined style={{ fontSize: 48, color: '#94a3b8', marginBottom: 12 }} />
    <Alert
      type="info"
      showIcon
      style={{ textAlign: 'left', maxWidth: 560, margin: '0 auto 16px' }}
      message={fileName || 'File'}
      description={
        <>
          <div>{reason || 'Opened with download fallback for this format.'}</div>
          <div style={{ marginTop: 8, color: '#64748b' }}>
            Type: {fileType || 'unknown'}
            {size != null ? ` · Size: ${formatBytes(size)}` : ''}
          </div>
        </>
      }
    />
    <Button type="primary" icon={<DownloadOutlined />} href={blobUrl} download={fileName || 'download'}>
      Download file
    </Button>
  </div>
)

const ImagePreview = ({ blobUrl, fileName, onBroken }) => {
  const [failed, setFailed] = useState(false)

  if (failed) {
    return (
      <DownloadFallback
        blobUrl={blobUrl}
        fileName={fileName}
        reason="This image format cannot be displayed in the browser. Download to open it."
      />
    )
  }

  return (
    <div
      className="drive-image-preview"
      style={{ textAlign: 'center', background: '#111827', minHeight: '60vh', padding: 16 }}>
      <img
        src={blobUrl}
        alt={fileName || 'Image preview'}
        style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        onError={() => {
          setFailed(true)
          onBroken?.()
        }}
      />
    </div>
  )
}

const MediaPreview = ({ blobUrl, kind, mime }) => (
  <div style={{ background: '#111827', minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
    {kind === 'video' ? (
      <video src={blobUrl} controls style={{ maxWidth: '100%', maxHeight: '80vh' }}>
        <track kind="captions" />
      </video>
    ) : (
      <audio src={blobUrl} controls style={{ width: '100%', maxWidth: 560 }} />
    )}
    <span style={{ display: 'none' }}>{mime}</span>
  </div>
)

const ExcelPreview = ({ arrayBuffer, blobUrl, fileName, fileType, fileSize }) => {
  const result = useMemo(() => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' })

      return {
        sheets: workbook.SheetNames.map((name) => {
          const sheet = workbook.Sheets[name]
          const html = XLSX.utils.sheet_to_html(sheet, { editable: false })

          return { name, html }
        })
      }
    } catch (err) {
      return { error: err?.message || 'Could not parse spreadsheet' }
    }
  }, [arrayBuffer])

  if (result.error) {
    return (
      <DownloadFallback
        blobUrl={blobUrl}
        fileName={fileName}
        fileType={fileType}
        size={fileSize}
        reason={result.error}
      />
    )
  }

  if (!result.sheets?.length) {
    return <Alert type="warning" message="Spreadsheet is empty" showIcon />
  }

  return (
    <div className="drive-excel-preview" style={{ background: '#fff', height: '85vh', overflow: 'auto', padding: 12 }}>
      <Tabs
        items={result.sheets.map((sheet) => ({
          key: sheet.name,
          label: sheet.name,
          children: <div className="drive-excel-sheet" dangerouslySetInnerHTML={{ __html: sheet.html }} />
        }))}
      />
    </div>
  )
}

const WordPreview = ({ arrayBuffer, fileName, blobUrl, fileType, fileSize }) => {
  const [html, setHtml] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const ext = getExt(fileName)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        if (ext === 'doc') {
          throw new Error(
            'Legacy .doc format cannot be previewed in the browser. Download the file or open it with OnlyOffice.'
          )
        }

        if (ext === 'odt') {
          const zip = await JSZip.loadAsync(arrayBuffer)
          const contentXml = await zip.file('content.xml')?.async('string')

          if (!contentXml) {
            throw new Error('Could not read OpenDocument content')
          }

          if (!cancelled) {
            setHtml(`<pre style="white-space:pre-wrap">${stripXml(contentXml)}</pre>`)
          }

          return
        }

        if (ext === 'rtf') {
          const text = new TextDecoder().decode(arrayBuffer)
          const plain = text
            .replace(/\\par[d]?/g, '\n')
            .replace(/\\'[0-9a-fA-F]{2}/g, '')
            .replace(/\\[a-z]+\d* ?/g, '')
            .replace(/[{}]/g, '')
            .trim()

          if (!cancelled) {
            setHtml(`<pre style="white-space:pre-wrap">${plain}</pre>`)
          }

          return
        }

        const mammoth = await import('mammoth')
        const result = await mammoth.convertToHtml({ arrayBuffer })

        if (!cancelled) {
          setHtml(result.value || '<p>(Empty document)</p>')
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Word preview failed')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [arrayBuffer, ext])

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Preparing document preview..." />
      </div>
    )
  }

  if (error) {
    return (
      <DownloadFallback
        blobUrl={blobUrl}
        fileName={fileName}
        fileType={fileType}
        size={fileSize}
        reason={error}
      />
    )
  }

  return (
    <div
      className="drive-word-preview"
      style={{ background: '#fff', height: '85vh', overflow: 'auto', padding: '24px 32px', lineHeight: 1.6 }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

const PptPreview = ({ arrayBuffer, fileName, blobUrl, fileType, fileSize }) => {
  const [slides, setSlides] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const ext = getExt(fileName)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)

      try {
        if (ext === 'ppt') {
          throw new Error(
            'Legacy .ppt format cannot be previewed in the browser. Download the file or open it with OnlyOffice.'
          )
        }

        const zip = await JSZip.loadAsync(arrayBuffer)

        if (ext === 'odp') {
          const contentXml = await zip.file('content.xml')?.async('string')
          const text = stripXml(contentXml || '')

          if (!cancelled) {
            setSlides([{ title: 'Presentation', text: text || '(No readable text)' }])
          }

          return
        }

        const slideFiles = Object.keys(zip.files)
          .filter((name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
          .sort((a, b) => {
            const na = Number(a.match(/slide(\d+)/i)?.[1] || 0)
            const nb = Number(b.match(/slide(\d+)/i)?.[1] || 0)

            return na - nb
          })

        const parsed = []

        for (const path of slideFiles) {
          const xml = await zip.file(path).async('string')
          parsed.push({
            title: `Slide ${parsed.length + 1}`,
            text: stripXml(xml) || '(No readable text on this slide)'
          })
        }

        if (!cancelled) {
          setSlides(parsed)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'PowerPoint preview failed')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [arrayBuffer, ext])

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Preparing presentation preview..." />
      </div>
    )
  }

  if (error) {
    return (
      <DownloadFallback
        blobUrl={blobUrl}
        fileName={fileName}
        fileType={fileType}
        size={fileSize}
        reason={error}
      />
    )
  }

  if (!slides.length) {
    return <Alert type="warning" message="No slides found in this presentation" showIcon />
  }

  return (
    <div className="drive-ppt-preview" style={{ background: '#f7f7f7', height: '85vh', overflow: 'auto', padding: 16 }}>
      {slides.map((slide) => (
        <div
          key={slide.title}
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 20,
            marginBottom: 16,
            minHeight: 160
          }}>
          <h4 style={{ marginTop: 0 }}>{slide.title}</h4>
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{slide.text}</p>
        </div>
      ))}
    </div>
  )
}

const ZipPreview = ({ arrayBuffer }) => {
  const [entries, setEntries] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    JSZip.loadAsync(arrayBuffer)
      .then((zip) => {
        if (cancelled) {
          return
        }

        const list = Object.keys(zip.files)
          .filter((name) => !zip.files[name].dir)
          .sort()
          .slice(0, 500)
          .map((name) => ({
            name,
            size: zip.files[name]._data?.uncompressedSize
          }))

        setEntries(list)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Could not read archive')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [arrayBuffer])

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Reading archive..." />
      </div>
    )
  }

  if (error) {
    return <Alert type="error" message="Archive preview failed" description={error} showIcon />
  }

  return (
    <div style={{ background: '#fff', height: '85vh', overflow: 'auto', padding: 16 }}>
      <h4 style={{ marginTop: 0 }}>Archive contents ({entries.length}{entries.length >= 500 ? '+' : ''})</h4>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {entries.map((item) => (
          <li key={item.name} style={{ marginBottom: 6 }}>
            {item.name}
            {item.size != null ? <span style={{ color: '#64748b' }}> — {formatBytes(item.size)}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

const TextPreview = ({ text }) => (
  <pre
    style={{
      background: '#fff',
      height: '85vh',
      overflow: 'auto',
      margin: 0,
      padding: 16,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }}>
    {text}
  </pre>
)

const HtmlPreview = ({ blobUrl, fileName }) => (
  <iframe
    title={fileName || 'HTML preview'}
    src={blobUrl}
    sandbox="allow-same-origin"
    style={{ width: '100%', height: '85vh', border: 'none', background: '#fff' }}
  />
)

const classify = (fileName, mime) => {
  const ext = getExt(fileName)
  const type = String(mime || '')

  if (type === 'application/pdf' || ext === 'pdf') {
    return 'pdf'
  }

  if (type.startsWith('image/') || IMAGE_EXTS.has(ext)) {
    return 'image'
  }

  if (type.startsWith('video/') || VIDEO_EXTS.has(ext)) {
    return 'video'
  }

  if (type.startsWith('audio/') || AUDIO_EXTS.has(ext)) {
    return 'audio'
  }

  if (EXCEL_EXTS.has(ext) || type.includes('spreadsheet') || type === 'application/vnd.ms-excel' || type === 'text/csv') {
    return 'excel'
  }

  if (WORD_EXTS.has(ext) || type.includes('wordprocessing') || type === 'application/msword' || type === 'application/rtf') {
    return 'word'
  }

  if (PPT_EXTS.has(ext) || type.includes('presentation') || type === 'application/vnd.ms-powerpoint') {
    return 'ppt'
  }

  if (ZIP_EXTS.has(ext) || type === 'application/zip' || type === 'application/x-zip-compressed') {
    return 'zip'
  }

  if (ext === 'html' || ext === 'htm' || type === 'text/html') {
    return 'html'
  }

  if (TEXT_EXTS.has(ext) || type.startsWith('text/') || type === 'application/json' || type === 'application/xml') {
    return 'text'
  }

  return 'unknown'
}

const RenderDocumentContent = ({ documentId, fileType, fileName, fileSize }) => {
  const [blobUrl, setBlobUrl] = useState(null)
  const [arrayBuffer, setArrayBuffer] = useState(null)
  const [textContent, setTextContent] = useState(null)
  const [resolvedType, setResolvedType] = useState(() => guessFileType(fileName, fileType))
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [kind, setKind] = useState('unknown')

  useEffect(() => {
    if (!documentId) {
      setError('Preview unavailable')
      setLoading(false)
      return undefined
    }

    let objectUrl
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      setBlobUrl(null)
      setArrayBuffer(null)
      setTextContent(null)

      try {
        const response = await apiClient.get(`/filestructure/preview-file/${documentId}`, {
          responseType: 'blob',
          skipLoader: true
        })
        const { data, headers } = response

        if (data?.type?.includes('application/json')) {
          const messageText = JSON.parse(await data.text())?.message || 'Preview failed'
          throw new Error(messageText)
        }

        const headerType = String(headers?.['content-type'] || headers?.['Content-Type'] || data?.type || '')
          .split(';')[0]
          .trim()
          .toLowerCase()
        const guessed = guessFileType(fileName, fileType || data.type)
        let provisionalKind = headerType === 'application/pdf' ? 'pdf' : classify(fileName, headerType || guessed)

        // Fast path: PDFs / media only need a blob URL (skip arrayBuffer copy).
        if (
          headerType === 'application/pdf' ||
          provisionalKind === 'pdf' ||
          provisionalKind === 'image' ||
          provisionalKind === 'video' ||
          provisionalKind === 'audio'
        ) {
          objectUrl = URL.createObjectURL(data)

          if (cancelled) {
            return
          }

          setResolvedType(headerType || guessed)
          setBlobUrl(objectUrl)
          setKind(provisionalKind === 'unknown' && headerType === 'application/pdf' ? 'pdf' : provisionalKind)
          return
        }

        const buffer = await data.arrayBuffer()
        const sniffed = sniffMimeFromBytes(buffer)
        const mime =
          (headerType && headerType !== 'application/octet-stream' && headerType) ||
          sniffed ||
          guessed
        const typedBlob = new Blob([buffer], { type: mime })
        objectUrl = URL.createObjectURL(typedBlob)

        if (cancelled) {
          return
        }

        let nextKind = classify(fileName, mime)

        if (headerType === 'application/pdf' || mime === 'application/pdf' || sniffed === 'application/pdf') {
          nextKind = 'pdf'
        }

        if (
          nextKind === 'zip' &&
          (WORD_EXTS.has(getExt(fileName)) || EXCEL_EXTS.has(getExt(fileName)) || PPT_EXTS.has(getExt(fileName)))
        ) {
          nextKind = classify(fileName, guessFileType(fileName, fileType))
        }

        if (nextKind === 'unknown' && looksLikeText(buffer)) {
          nextKind = 'text'
        }

        setResolvedType(mime)
        setArrayBuffer(buffer)
        setBlobUrl(objectUrl)
        setKind(nextKind)

        if (nextKind === 'text' || nextKind === 'html') {
          setTextContent(new TextDecoder().decode(buffer))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || 'Preview failed')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [documentId, fileName, fileType])

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin tip="Loading preview..." />
      </div>
    )
  }

  if (error) {
    return <Alert type="error" message="Preview failed" description={error} showIcon />
  }

  if (kind === 'pdf' && blobUrl) {
    return (
      <iframe
        className="drive-pdf-preview"
        src={blobUrl}
        title={fileName || 'PDF preview'}
        style={{ width: '100%', height: '85vh', border: 'none', background: '#fff' }}
      />
    )
  }

  if (kind === 'image' && blobUrl) {
    return <ImagePreview blobUrl={blobUrl} fileName={fileName} />
  }

  if (kind === 'video' && blobUrl) {
    return <MediaPreview blobUrl={blobUrl} kind="video" mime={resolvedType} />
  }

  if (kind === 'audio' && blobUrl) {
    return <MediaPreview blobUrl={blobUrl} kind="audio" mime={resolvedType} />
  }

  if (kind === 'excel' && arrayBuffer) {
    return (
      <ExcelPreview
        arrayBuffer={arrayBuffer}
        blobUrl={blobUrl}
        fileName={fileName}
        fileType={resolvedType}
        fileSize={fileSize || arrayBuffer?.byteLength}
      />
    )
  }

  if (kind === 'word' && arrayBuffer) {
    return (
      <WordPreview
        arrayBuffer={arrayBuffer}
        fileName={fileName}
        blobUrl={blobUrl}
        fileType={resolvedType}
        fileSize={fileSize || arrayBuffer?.byteLength}
      />
    )
  }

  if (kind === 'ppt' && arrayBuffer) {
    return (
      <PptPreview
        arrayBuffer={arrayBuffer}
        fileName={fileName}
        blobUrl={blobUrl}
        fileType={resolvedType}
        fileSize={fileSize || arrayBuffer?.byteLength}
      />
    )
  }

  if (kind === 'zip' && arrayBuffer) {
    return <ZipPreview arrayBuffer={arrayBuffer} />
  }

  if (kind === 'html' && blobUrl) {
    return <HtmlPreview blobUrl={blobUrl} fileName={fileName} />
  }

  if (kind === 'text' && textContent != null) {
    return <TextPreview text={textContent} />
  }

  if (blobUrl) {
    // Last resort: let the browser try, plus download
    return (
      <div>
        <iframe
          title={fileName || 'File preview'}
          src={blobUrl}
          style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }}
        />
        <DownloadFallback
          blobUrl={blobUrl}
          fileName={fileName}
          fileType={resolvedType}
          size={fileSize || arrayBuffer?.byteLength}
          reason="If the preview above is blank, download the file to open it in a desktop app."
        />
      </div>
    )
  }

  return <Alert type="warning" message="Preview unavailable" showIcon />
}

export default memo(RenderDocumentContent)
